/**
 * Bar charts of the report screens and PDFs (UI phase D, ADR 0031). One pure layout feeds the React chart
 * (screen) and an SVG string (PDF, drawn by pdfmake's SVG support inside the export Web Worker), so the
 * picture on screen and on paper is the same. No DOM, no chart library.
 *
 * A spec carries only numbers the report rows already contain (see report-figures.ts) and labels that
 * are already translated; it is plain data, so it can be posted to the export worker.
 */
import { formatNum, formatPct } from "./report-figures";

/**
 * Chart colours. The screen uses the CSS tokens (tokens.css is the only place colours are written for the
 * UI); the PDF cannot read CSS, so the same values are listed here and a test keeps both in step.
 */
export const CHART_COLORS = {
  accent: { token: "--chart-accent", hex: "#f96915" },
  ink: { token: "--chart-ink", hex: "#4d5052" },
  muted: { token: "--chart-muted", hex: "#c4c4bf" },
  ramp1: { token: "--chart-ramp-1", hex: "#fdd9bd" },
  ramp2: { token: "--chart-ramp-2", hex: "#fba765" },
  ramp3: { token: "--chart-ramp-3", hex: "#f96915" },
  ramp4: { token: "--chart-ramp-4", hex: "#a8460a" },
  crit: { token: "--chart-crit", hex: "#a82c23" },
  grid: { token: "--chart-grid", hex: "#e2e2df" },
  axis: { token: "--chart-axis", hex: "#5e6163" },
} as const;
export type ChartColor = keyof typeof CHART_COLORS;

export type ChartSeries = { label: string; color: ChartColor };
/** One category: a short axis label, an optional longer title (tooltip), one value per series. */
export type ChartBarSpec = { key: string; label: string; title?: string; values: number[] };

export type ChartSpec = {
  orientation: "vertical" | "horizontal";
  series: ChartSeries[];
  bars: ChartBarSpec[];
  /** Fixed top of the value axis (e.g. 100 for shares); otherwise a round number above the largest bar. */
  max?: number;
  /** Values are percentages ("%35,6" / "35.6%"). */
  percent?: boolean;
  /** Unit after the value ("sa", "h", "adet"); ignored for percentages. */
  unit?: string;
  digits: number;
  locale: string;
  /** Text alternative: one sentence about what the chart shows. */
  summary: string;
  /** Tooltip / table: the "total" label (stacked charts with more than one series). */
  totalLabel?: string;
  /** Data table: the header of the category column ("Gün", "Araç"). */
  categoryLabel?: string;
};

export function formatChartValue(spec: Pick<ChartSpec, "percent" | "unit" | "digits" | "locale">, v: number): string {
  if (spec.percent) return formatPct(v, spec.locale, spec.digits);
  const n = formatNum(v, spec.locale, spec.digits);
  return spec.unit ? `${n} ${spec.unit}` : n;
}

/** Round tick values from 0 to at least `max` (at most ~5 steps of 1, 2, 2.5 or 5 × 10^k). */
export function niceTicks(max: number, target = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / target;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? 10 * pow;
  const top = Math.ceil(max / step - 1e-9) * step;
  const out: number[] = [];
  for (let v = 0; v <= top + step / 2; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

export type Rect = { x: number; y: number; w: number; h: number };
export type LaidBar = {
  key: string;
  label: string;
  title: string;
  /** Whether the axis label is drawn (labels are thinned when they would overlap). */
  showLabel: boolean;
  /** Label anchor: centre of the band (vertical) or the row's middle (horizontal). */
  lx: number;
  ly: number;
  /** The whole band (hover / tap target). */
  band: Rect;
  segs: (Rect & { series: number; value: number })[];
  total: number;
};
export type ChartLayout = {
  width: number;
  height: number;
  plot: Rect;
  domainMax: number;
  ticks: { value: number; pos: number; label: string }[];
  bars: LaidBar[];
};

/** Rough text width without a DOM (Inter / Noto Sans at `size` px: ~0.56 em per character). */
export const textWidth = (s: string, size = 12) => s.length * size * 0.56;

const V_HEIGHT = 220;
const ROW_H = 26;

/** Places every bar, tick and label for a chart `width` px wide. */
export function layoutChart(spec: ChartSpec, width: number, opts: { fontSize?: number; height?: number; rowH?: number } = {}): ChartLayout {
  const fs = opts.fontSize ?? 12;
  const totals = spec.bars.map((b) => b.values.reduce((a, v) => a + Math.max(0, v), 0));
  const dataMax = Math.max(0, ...totals);
  const ticksV = spec.max !== undefined ? niceTicks(spec.max).filter((v) => v <= spec.max! + 1e-9) : niceTicks(dataMax);
  const domainMax = spec.max ?? ticksV[ticksV.length - 1]!;
  const tickText = (v: number) => (spec.percent ? formatPct(v, spec.locale, 0) : formatNum(v, spec.locale, Number.isInteger(v) ? 0 : 1));
  const n = Math.max(1, spec.bars.length);

  if (spec.orientation === "vertical") {
    const left = Math.ceil(Math.max(...ticksV.map((v) => textWidth(tickText(v), fs)))) + 10;
    const height = opts.height ?? V_HEIGHT;
    const plot: Rect = { x: left, y: 8, w: Math.max(10, width - left - 8), h: height - 8 - (fs + 14) };
    const bandW = plot.w / n;
    const barW = Math.max(1, Math.min(48, bandW * 0.7));
    const widest = Math.max(1, ...spec.bars.map((b) => textWidth(b.label, fs)));
    const step = Math.max(1, Math.ceil((widest + 8) / bandW));
    const y = (v: number) => plot.y + plot.h - (domainMax > 0 ? (v / domainMax) * plot.h : 0);
    const bars = spec.bars.map((b, i) => {
      const cx = plot.x + bandW * (i + 0.5);
      let acc = 0;
      const segs = b.values.map((v, s) => {
        const val = Math.max(0, v);
        const top = y(acc + val);
        const bottom = y(acc);
        acc += val;
        return { x: cx - barW / 2, y: top, w: barW, h: Math.max(0, bottom - top), series: s, value: v };
      });
      return {
        key: b.key, label: b.label, title: b.title ?? b.label, showLabel: i % step === 0,
        lx: cx, ly: plot.y + plot.h + fs + 4,
        band: { x: plot.x + bandW * i, y: plot.y, w: bandW, h: plot.h },
        segs, total: totals[i]!,
      };
    });
    return { width, height, plot, domainMax, ticks: ticksV.map((v) => ({ value: v, pos: y(v), label: tickText(v) })), bars };
  }

  const labelW = Math.min(Math.max(60, width * 0.34), Math.ceil(Math.max(0, ...spec.bars.map((b) => textWidth(b.label, fs)))) + 12);
  const rowH = opts.rowH ?? ROW_H;
  const plot: Rect = { x: labelW, y: 4, w: Math.max(10, width - labelW - 16), h: n * rowH };
  const height = plot.y + plot.h + fs + 12;
  const x = (v: number) => plot.x + (domainMax > 0 ? (v / domainMax) * plot.w : 0);
  const barH = rowH * 0.64;
  const bars = spec.bars.map((b, i) => {
    const cy = plot.y + rowH * (i + 0.5);
    let acc = 0;
    const segs = b.values.map((v, s) => {
      const val = Math.max(0, v);
      const l = x(acc);
      const r = x(acc + val);
      acc += val;
      return { x: l, y: cy - barH / 2, w: Math.max(0, r - l), h: barH, series: s, value: v };
    });
    return {
      key: b.key, label: b.label, title: b.title ?? b.label, showLabel: true,
      lx: labelW - 8, ly: cy,
      band: { x: 0, y: plot.y + rowH * i, w: width, h: rowH },
      segs, total: totals[i]!,
    };
  });
  return { width, height, plot, domainMax, ticks: ticksV.map((v) => ({ value: v, pos: x(v), label: tickText(v) })), bars };
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const r1 = (v: number) => Math.round(v * 10) / 10;

/**
 * The chart as an SVG document for the PDF (sizes in pt): legend on top, then the bars. Text uses the
 * PDF's embedded font family.
 */
export function chartSvg(spec: ChartSpec, width: number, font = "NotoSans"): string {
  const fs = 7.5;
  const legendH = spec.series.length > 1 ? 16 : 0;
  // Horizontal charts: smaller rows for longer lists (the PDF passes at most PDF_CHART_MAX_ROWS, one page).
  const rowH = Math.max(9, Math.min(18, 360 / Math.max(1, spec.bars.length)));
  const lay = layoutChart(spec, width, { fontSize: Math.min(fs, rowH * 0.8), height: 170, rowH });
  const out: string[] = [];
  const H = lay.height + legendH;
  out.push(`<svg xmlns="http://www.w3.org/2000/svg" width="${r1(width)}" height="${r1(H)}" viewBox="0 0 ${r1(width)} ${r1(H)}" font-family="${font}" font-size="${fs}">`);
  if (legendH) {
    let lx = lay.plot.x;
    for (const s of spec.series) {
      out.push(`<rect x="${r1(lx)}" y="3" width="9" height="9" fill="${CHART_COLORS[s.color].hex}"/>`);
      out.push(`<text x="${r1(lx + 13)}" y="10.5" fill="${CHART_COLORS.axis.hex}">${esc(s.label)}</text>`);
      lx += 13 + textWidth(s.label, fs) + 16;
    }
  }
  out.push(`<g transform="translate(0 ${legendH})">`);
  const vertical = spec.orientation === "vertical";
  for (const t of lay.ticks) {
    if (vertical) {
      out.push(`<line x1="${r1(lay.plot.x)}" x2="${r1(lay.plot.x + lay.plot.w)}" y1="${r1(t.pos)}" y2="${r1(t.pos)}" stroke="${CHART_COLORS.grid.hex}" stroke-width="0.6"/>`);
      out.push(`<text x="${r1(lay.plot.x - 5)}" y="${r1(t.pos + 2.5)}" text-anchor="end" fill="${CHART_COLORS.axis.hex}">${esc(t.label)}</text>`);
    } else {
      out.push(`<line x1="${r1(t.pos)}" x2="${r1(t.pos)}" y1="${r1(lay.plot.y)}" y2="${r1(lay.plot.y + lay.plot.h)}" stroke="${CHART_COLORS.grid.hex}" stroke-width="0.6"/>`);
      out.push(`<text x="${r1(t.pos)}" y="${r1(lay.plot.y + lay.plot.h + fs + 4)}" text-anchor="middle" fill="${CHART_COLORS.axis.hex}">${esc(t.label)}</text>`);
    }
  }
  for (const b of lay.bars) {
    for (const s of b.segs) {
      if (s.w <= 0 || s.h <= 0) continue;
      out.push(`<rect x="${r1(s.x)}" y="${r1(s.y)}" width="${r1(s.w)}" height="${r1(s.h)}" fill="${CHART_COLORS[spec.series[s.series]!.color].hex}"/>`);
    }
    if (b.showLabel) {
      out.push(vertical
        ? `<text x="${r1(b.lx)}" y="${r1(b.ly)}" text-anchor="middle" fill="${CHART_COLORS.axis.hex}">${esc(b.label)}</text>`
        : `<text x="${r1(b.lx)}" y="${r1(b.ly + 2.5)}" text-anchor="end" fill="${CHART_COLORS.axis.hex}">${esc(b.label)}</text>`);
    }
  }
  // Baseline.
  out.push(vertical
    ? `<line x1="${r1(lay.plot.x)}" x2="${r1(lay.plot.x + lay.plot.w)}" y1="${r1(lay.plot.y + lay.plot.h)}" y2="${r1(lay.plot.y + lay.plot.h)}" stroke="${CHART_COLORS.axis.hex}" stroke-width="0.8"/>`
    : `<line x1="${r1(lay.plot.x)}" x2="${r1(lay.plot.x)}" y1="${r1(lay.plot.y)}" y2="${r1(lay.plot.y + lay.plot.h)}" stroke="${CHART_COLORS.axis.hex}" stroke-width="0.8"/>`);
  out.push("</g></svg>");
  return out.join("");
}
